import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { BookOpen, Calendar, Heart, MessageCircle, Volume2, Flame, Star, Quote } from 'lucide-react';
import TreeLogo from '@/components/TreeLogo';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

// Get current season based on month
const getCurrentSeason = () => {
  const month = new Date().getMonth();
  if (month >= 2 && month <= 4) return 'spring'; // March-May
  if (month >= 5 && month <= 7) return 'summer'; // June-August
  if (month >= 8 && month <= 10) return 'autumn'; // September-November
  return 'winter'; // December-February
};

// Animated Growing Branch Component with Seasonal Variations
const SeasonalGrowingBranch = ({ className, style, delay = 0, direction = 'left', scale = 1, season = null, angle = 0 }) => {
  const isLeft = direction === 'left';
  const currentSeason = season || getCurrentSeason();
  
  // Calculate rotation angle - branches grow diagonally
  const rotationAngle = isLeft ? (angle || -25) : (angle || 25);
  
  // Season-specific colors
  const seasonColors = {
    spring: {
      trunk: 'hsl(30 35% 35%)',
      branch: 'hsl(30 35% 40%)',
      twig: 'hsl(30 35% 45%)',
      leaf: 'hsl(340 70% 75%)', // Cherry blossom pink
      leafAlt: 'hsl(340 65% 80%)',
      leafSmall: 'hsl(100 40% 55%)'
    },
    summer: {
      trunk: 'hsl(30 40% 32%)',
      branch: 'hsl(30 40% 38%)',
      twig: 'hsl(30 40% 42%)',
      leaf: 'hsl(120 45% 40%)', // Rich green
      leafAlt: 'hsl(100 50% 45%)',
      leafSmall: 'hsl(80 45% 50%)'
    },
    autumn: {
      trunk: 'hsl(25 45% 30%)',
      branch: 'hsl(25 45% 35%)',
      twig: 'hsl(25 45% 40%)',
      leaf: 'hsl(25 80% 50%)', // Orange
      leafAlt: 'hsl(0 70% 55%)', // Red
      leafSmall: 'hsl(45 85% 55%)' // Golden yellow
    },
    winter: {
      trunk: 'hsl(220 15% 35%)',
      branch: 'hsl(220 15% 40%)',
      twig: 'hsl(220 15% 45%)',
      leaf: 'hsl(200 30% 85%)', // Frost/snow
      leafAlt: 'hsl(210 25% 90%)',
      leafSmall: 'hsl(195 35% 88%)'
    }
  };
  
  const colors = seasonColors[currentSeason];
  
  return (
    <svg 
      className={`absolute pointer-events-none ${className}`}
      style={{ 
        ...style, 
        transform: `scale(${scale}) ${isLeft ? '' : 'scaleX(-1)'} rotate(${rotationAngle}deg)`,
        transformOrigin: isLeft ? 'bottom left' : 'bottom right'
      }}
      width="350" 
      height="500" 
      viewBox="0 0 350 500"
    >
      {/* Main trunk growing upward */}
      <path 
        d="M50 500 Q40 400 60 320 Q80 240 50 170 Q20 100 60 40 Q100 -20 80 -60"
        stroke={colors.trunk}
        strokeWidth="7" 
        fill="none"
        strokeLinecap="round"
        style={{
          strokeDasharray: 700,
          strokeDashoffset: 700,
          animation: `growBranch 5s ease-out ${delay}s forwards`
        }}
      />
      {/* Major branch 1 */}
      <path 
        d="M60 320 Q100 290 140 310 Q180 330 210 300 Q240 270 260 290"
        stroke={colors.branch}
        strokeWidth="4" 
        fill="none"
        strokeLinecap="round"
        style={{
          strokeDasharray: 300,
          strokeDashoffset: 300,
          animation: `growBranch 3s ease-out ${delay + 1.2}s forwards`
        }}
      />
      {/* Major branch 2 */}
      <path 
        d="M50 170 Q100 140 150 170 Q200 200 240 170 Q280 140 300 160"
        stroke={colors.branch}
        strokeWidth="4" 
        fill="none"
        strokeLinecap="round"
        style={{
          strokeDasharray: 350,
          strokeDashoffset: 350,
          animation: `growBranch 3s ease-out ${delay + 1.8}s forwards`
        }}
      />
      {/* Small twig 1 */}
      <path 
        d="M140 310 Q160 280 180 295"
        stroke={colors.twig}
        strokeWidth="2.5" 
        fill="none"
        strokeLinecap="round"
        style={{
          strokeDasharray: 80,
          strokeDashoffset: 80,
          animation: `growBranch 2s ease-out ${delay + 2.5}s forwards`
        }}
      />
      {/* Small twig 2 */}
      <path 
        d="M150 170 Q170 140 190 155"
        stroke={colors.twig}
        strokeWidth="2.5" 
        fill="none"
        strokeLinecap="round"
        style={{
          strokeDasharray: 80,
          strokeDashoffset: 80,
          animation: `growBranch 2s ease-out ${delay + 2.8}s forwards`
        }}
      />
      {/* Small twig 3 */}
      <path 
        d="M240 170 Q260 150 275 165"
        stroke={colors.twig}
        strokeWidth="2" 
        fill="none"
        strokeLinecap="round"
        style={{
          strokeDasharray: 60,
          strokeDashoffset: 60,
          animation: `growBranch 2s ease-out ${delay + 3.2}s forwards`
        }}
      />
      {/* Root spreading */}
      <path 
        d="M50 500 Q30 480 20 450 Q10 420 30 400"
        stroke={colors.trunk}
        strokeWidth="4" 
        fill="none"
        strokeLinecap="round"
        style={{
          strokeDasharray: 150,
          strokeDashoffset: 150,
          animation: `growBranch 2.5s ease-out ${delay + 0.6}s forwards`
        }}
      />
      {/* Root 2 */}
      <path 
        d="M50 500 Q80 470 100 490"
        stroke={colors.branch}
        strokeWidth="3" 
        fill="none"
        strokeLinecap="round"
        style={{
          strokeDasharray: 100,
          strokeDashoffset: 100,
          animation: `growBranch 2s ease-out ${delay + 1}s forwards`
        }}
      />
      
      {/* Season-specific decorations */}
      {currentSeason === 'spring' && (
        <>
          {/* Cherry blossoms */}
          <circle cx="260" cy="290" r="6" fill={colors.leaf}
            style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 3.8}s forwards` }}
          />
          <circle cx="268" cy="285" r="5" fill={colors.leafAlt}
            style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 4}s forwards` }}
          />
          <circle cx="300" cy="160" r="6" fill={colors.leaf}
            style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 4.2}s forwards` }}
          />
          <circle cx="308" cy="155" r="5" fill={colors.leafAlt}
            style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 4.4}s forwards` }}
          />
          <circle cx="180" cy="295" r="5" fill={colors.leaf}
            style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 4.1}s forwards` }}
          />
          <circle cx="190" cy="155" r="5" fill={colors.leaf}
            style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 4.3}s forwards` }}
          />
          <circle cx="275" cy="165" r="4" fill={colors.leafAlt}
            style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 4.5}s forwards` }}
          />
          {/* Small green leaves */}
          <circle cx="145" cy="305" r="3" fill={colors.leafSmall}
            style={{ opacity: 0, animation: `fadeInLeaf 1s ease-out ${delay + 4.6}s forwards` }}
          />
        </>
      )}
      
      {currentSeason === 'summer' && (
        <>
          {/* Lush green leaves */}
          <ellipse cx="260" cy="290" rx="8" ry="5" fill={colors.leaf}
            style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 3.8}s forwards`, transform: 'rotate(-20deg)', transformOrigin: '260px 290px' }}
          />
          <ellipse cx="300" cy="160" rx="8" ry="5" fill={colors.leafAlt}
            style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 4}s forwards`, transform: 'rotate(15deg)', transformOrigin: '300px 160px' }}
          />
          <ellipse cx="180" cy="295" rx="7" ry="4" fill={colors.leaf}
            style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 4.2}s forwards` }}
          />
          <ellipse cx="190" cy="155" rx="7" ry="4" fill={colors.leafSmall}
            style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 4.4}s forwards` }}
          />
          <ellipse cx="275" cy="165" rx="6" ry="3" fill={colors.leaf}
            style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 4.6}s forwards` }}
          />
          <ellipse cx="250" cy="300" rx="6" ry="3" fill={colors.leafAlt}
            style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 4.3}s forwards` }}
          />
        </>
      )}
      
      {currentSeason === 'autumn' && (
        <>
          {/* Autumn colored leaves - some falling */}
          <ellipse cx="260" cy="290" rx="6" ry="4" fill={colors.leaf}
            style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 3.8}s forwards` }}
          />
          <ellipse cx="300" cy="160" rx="6" ry="4" fill={colors.leafAlt}
            style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 4}s forwards` }}
          />
          <ellipse cx="180" cy="295" rx="5" ry="3" fill={colors.leafSmall}
            style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 4.2}s forwards` }}
          />
          <ellipse cx="190" cy="155" rx="5" ry="3" fill={colors.leaf}
            style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 4.4}s forwards` }}
          />
          <ellipse cx="275" cy="165" rx="4" ry="3" fill={colors.leafAlt}
            style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 4.6}s forwards` }}
          />
          {/* Falling leaves */}
          <ellipse cx="230" cy="220" rx="4" ry="3" fill={colors.leafSmall}
            style={{ opacity: 0, animation: `leafFall 4s ease-in-out ${delay + 5}s infinite` }}
          />
          <ellipse cx="280" cy="180" rx="3" ry="2" fill={colors.leafAlt}
            style={{ opacity: 0, animation: `leafFall 5s ease-in-out ${delay + 5.5}s infinite` }}
          />
        </>
      )}
      
      {currentSeason === 'winter' && (
        <>
          {/* Snow/frost on branches */}
          <ellipse cx="260" cy="288" rx="10" ry="3" fill={colors.leaf}
            style={{ opacity: 0, animation: `fadeInLeaf 1.5s ease-out ${delay + 3.8}s forwards` }}
          />
          <ellipse cx="300" cy="158" rx="8" ry="3" fill={colors.leafAlt}
            style={{ opacity: 0, animation: `fadeInLeaf 1.5s ease-out ${delay + 4}s forwards` }}
          />
          <ellipse cx="180" cy="293" rx="6" ry="2" fill={colors.leaf}
            style={{ opacity: 0, animation: `fadeInLeaf 1.5s ease-out ${delay + 4.2}s forwards` }}
          />
          <ellipse cx="60" cy="318" rx="8" ry="3" fill={colors.leafSmall}
            style={{ opacity: 0, animation: `fadeInLeaf 1.5s ease-out ${delay + 4.4}s forwards` }}
          />
          <ellipse cx="50" cy="168" rx="7" ry="2" fill={colors.leaf}
            style={{ opacity: 0, animation: `fadeInLeaf 1.5s ease-out ${delay + 4.6}s forwards` }}
          />
        </>
      )}
    </svg>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [testimonialForm, setTestimonialForm] = useState({
    name: '',
    location: '',
    rating: 5,
    content: '',
    email: ''
  });

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials/public?limit=6`);
      setTestimonials(response.data.testimonials);
    } catch (error) {
      console.error('Failed to load testimonials');
    }
  };

  const handleSubmitTestimonial = async () => {
    if (!testimonialForm.name.trim() || !testimonialForm.content.trim()) {
      toast.error('Please fill in your name and testimonial');
      return;
    }
    if (testimonialForm.content.length < 10) {
      toast.error('Please write at least 10 characters');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/testimonials/submit`, testimonialForm);
      toast.success('Thank you! Your testimonial has been submitted for review.');
      setShowSubmitDialog(false);
      setTestimonialForm({ name: '', location: '', rating: 5, content: '', email: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit testimonial');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating, interactive = false, onChange = null) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-5 h-5 ${i < rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'} ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
        onClick={interactive ? () => onChange(i + 1) : undefined}
      />
    ));
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section with colorful gradient */}
      <section className="relative min-h-screen flex items-center justify-center px-8 overflow-hidden">
        {/* Softer gradient background */}
        <div 
          className="absolute inset-0"
          style={{ 
            background: 'linear-gradient(135deg, hsl(250 12% 88%) 0%, hsl(250 10% 92%) 25%, hsl(45 30% 95%) 50%, hsl(15 35% 93%) 75%, hsl(250 10% 90%) 100%)'
          }}
        ></div>
        
        {/* Animated Growing Branches - Bottom Left */}
        <SeasonalGrowingBranch 
          direction="left" 
          delay={0.5}
          scale={1.2}
          className="opacity-[0.15]"
          style={{ left: '-80px', bottom: '-100px' }}
        />
        
        {/* Animated Growing Branches - Bottom Right */}
        <SeasonalGrowingBranch 
          direction="right" 
          delay={1}
          scale={1.2}
          className="opacity-[0.15]"
          style={{ right: '-80px', bottom: '-100px' }}
        />
        
        {/* Decorative circles - muted */}
        <div className="absolute top-20 left-1/4 w-64 h-64 rounded-full opacity-20" style={{ backgroundColor: 'hsl(250 15% 78%)' }}></div>
        <div className="absolute bottom-32 right-1/4 w-48 h-48 rounded-full opacity-15" style={{ backgroundColor: 'hsl(15 35% 82%)' }}></div>
        <div className="absolute top-1/3 right-1/3 w-32 h-32 rounded-full opacity-12" style={{ backgroundColor: 'hsl(45 40% 80%)' }}></div>
        <div className="absolute bottom-1/4 left-1/3 w-40 h-40 rounded-full opacity-12" style={{ backgroundColor: 'hsl(250 12% 75%)' }}></div>
        
        <div className="relative z-10 max-w-3xl mx-auto text-center fade-in">
          {/* Tree Logo */}
          <div className="mb-8 inline-flex items-center justify-center w-28 h-28 rounded-full" style={{ backgroundColor: 'hsl(15 35% 85%)' }}>
            <TreeLogo size={56} className="text-slate-600" />
          </div>
          
          <h1 className="text-6xl sm:text-7xl lg:text-8xl font-light mb-6" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 40%)' }}>
            Rooted
          </h1>
          <p className="text-2xl sm:text-3xl mb-4 font-light" style={{ color: 'hsl(250 12% 50%)' }}>
            A 365-Day Devotional Journey
          </p>
          <p className="text-lg mb-12 max-w-2xl mx-auto leading-relaxed" style={{ color: 'hsl(220 12% 45%)' }}>
            Experience daily spiritual growth through liturgical tradition, sacred scripture, and personal reflection.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              data-testid="get-started-btn"
              size="lg" 
              className="rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-shadow"
              style={{ backgroundColor: 'hsl(250 15% 60%)', color: 'white' }}
              onClick={() => navigate('/subscribe')}
            >
              Begin Your Journey
            </Button>
            <Button 
              data-testid="preview-btn"
              size="lg" 
              variant="outline" 
              className="rounded-full px-8 py-6 text-lg border-2"
              style={{ borderColor: 'hsl(250 12% 72%)', color: 'hsl(250 15% 45%)', backgroundColor: 'white/50' }}
              onClick={() => navigate('/preview')}
            >
              See Preview
            </Button>
            <Button 
              data-testid="login-btn"
              size="lg" 
              variant="ghost" 
              className="rounded-full px-8 py-6 text-lg"
              style={{ color: 'hsl(250 15% 45%)' }}
              onClick={() => navigate('/login')}
            >
              Sign In
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section with soft lavender background */}
      <section className="py-24 px-8" style={{ backgroundColor: 'hsl(250 12% 95%)' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl sm:text-5xl text-center mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 40%)' }}>
            A Sanctuary for Daily Prayer
          </h2>
          <p className="text-center mb-16 text-lg" style={{ color: 'hsl(220 12% 50%)' }}>
            Everything you need for a meaningful devotional practice
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="p-8 text-center fade-in border-0 rounded-2xl" style={{ backgroundColor: 'white' }}>
              <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full" style={{ backgroundColor: 'hsl(250 12% 92%)' }}>
                <BookOpen className="w-8 h-8" style={{ color: 'hsl(250 15% 50%)' }} strokeWidth={1.5} />
              </div>
              <h3 className="text-xl mb-3 font-medium" style={{ color: 'hsl(250 15% 35%)' }}>Daily Devotionals</h3>
              <p className="leading-relaxed" style={{ color: 'hsl(220 12% 50%)' }}>
                365 carefully crafted reflections aligned with the liturgical calendar
              </p>
            </Card>
            
            <Card className="p-8 text-center fade-in border-0 rounded-2xl" style={{ backgroundColor: 'white', animationDelay: '100ms' }}>
              <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full" style={{ backgroundColor: 'hsl(15 35% 93%)' }}>
                <Calendar className="w-8 h-8" style={{ color: 'hsl(15 40% 55%)' }} strokeWidth={1.5} />
              </div>
              <h3 className="text-xl mb-3 font-medium" style={{ color: 'hsl(250 15% 35%)' }}>Liturgical Seasons</h3>
              <p className="leading-relaxed" style={{ color: 'hsl(220 12% 50%)' }}>
                Journey through Advent, Lent, Easter, and Ordinary Time
              </p>
            </Card>
            
            <Card className="p-8 text-center fade-in border-0 rounded-2xl" style={{ backgroundColor: 'white', animationDelay: '200ms' }}>
              <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full" style={{ backgroundColor: 'hsl(45 45% 92%)' }}>
                <MessageCircle className="w-8 h-8" style={{ color: 'hsl(45 50% 45%)' }} strokeWidth={1.5} />
              </div>
              <h3 className="text-xl mb-3 font-medium" style={{ color: 'hsl(250 15% 35%)' }}>Personal Journal</h3>
              <p className="leading-relaxed" style={{ color: 'hsl(220 12% 50%)' }}>
                Reflect and respond to daily writing prompts
              </p>
            </Card>
            
            <Card className="p-8 text-center fade-in border-0 rounded-2xl" style={{ backgroundColor: 'white', animationDelay: '300ms' }}>
              <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full" style={{ backgroundColor: 'hsl(250 12% 92%)' }}>
                <Volume2 className="w-8 h-8" style={{ color: 'hsl(250 15% 50%)' }} strokeWidth={1.5} />
              </div>
              <h3 className="text-xl mb-3 font-medium" style={{ color: 'hsl(250 15% 35%)' }}>Audio Support</h3>
              <p className="leading-relaxed" style={{ color: 'hsl(220 12% 50%)' }}>
                Listen to devotionals with high-quality text-to-speech
              </p>
            </Card>
            
            <Card className="p-8 text-center fade-in border-0 rounded-2xl" style={{ backgroundColor: 'white', animationDelay: '400ms' }}>
              <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full" style={{ backgroundColor: 'hsl(15 35% 93%)' }}>
                <Heart className="w-8 h-8" style={{ color: 'hsl(15 40% 55%)' }} strokeWidth={1.5} />
              </div>
              <h3 className="text-xl mb-3 font-medium" style={{ color: 'hsl(250 15% 35%)' }}>Save Favorites</h3>
              <p className="leading-relaxed" style={{ color: 'hsl(220 12% 50%)' }}>
                Bookmark meaningful devotionals to revisit anytime
              </p>
            </Card>
            
            <Card className="p-8 text-center fade-in border-0 rounded-2xl" style={{ backgroundColor: 'white', animationDelay: '500ms' }}>
              <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full" style={{ backgroundColor: 'hsl(45 45% 92%)' }}>
                <Flame className="w-8 h-8" style={{ color: 'hsl(45 50% 45%)' }} strokeWidth={1.5} />
              </div>
              <h3 className="text-xl mb-3 font-medium" style={{ color: 'hsl(250 15% 35%)' }}>Reading Streaks</h3>
              <p className="leading-relaxed" style={{ color: 'hsl(220 12% 50%)' }}>
                Stay motivated with streak tracking and reminders
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 px-8 relative overflow-hidden" style={{ backgroundColor: 'hsl(250 10% 96%)' }}>
        {/* Animated Seasonal Growing Branches - Left Side */}
        <SeasonalGrowingBranch 
          direction="left" 
          delay={0}
          scale={1.3}
          className="opacity-[0.15]"
          style={{ left: '-100px', bottom: '-80px' }}
        />
        
        {/* Animated Seasonal Growing Branches - Right Side */}
        <SeasonalGrowingBranch 
          direction="right" 
          delay={0.5}
          scale={1.3}
          className="opacity-[0.15]"
          style={{ right: '-100px', bottom: '-80px' }}
        />
        
        {/* Subtle organic shapes */}
        <div className="absolute top-10 right-20 w-40 h-40 rounded-full opacity-10" style={{ backgroundColor: 'hsl(15 35% 75%)' }}></div>
        <div className="absolute bottom-20 left-10 w-32 h-32 rounded-full opacity-12" style={{ backgroundColor: 'hsl(45 40% 75%)' }}></div>
        
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Quote className="w-12 h-12 mx-auto mb-4" style={{ color: 'hsl(250 15% 70%)' }} strokeWidth={1} />
            <h2 className="text-4xl sm:text-5xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 40%)' }}>
              What Our Community Says
            </h2>
            <p className="text-lg" style={{ color: 'hsl(220 12% 50%)' }}>
              Join thousands growing in their faith journey
            </p>
          </div>
          
          {testimonials.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {testimonials.map((t) => (
                <Card 
                  key={t.testimonial_id} 
                  className="p-6 rounded-2xl border-0 shadow-md hover:shadow-lg transition-shadow"
                  style={{ backgroundColor: 'white' }}
                  data-testid={`public-testimonial-${t.testimonial_id}`}
                >
                  <div className="flex items-center gap-1 mb-3">
                    {renderStars(t.rating)}
                  </div>
                  <p className="text-base mb-4 leading-relaxed" style={{ color: 'hsl(220 12% 40%)', fontStyle: 'italic' }}>
                    "{t.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium" style={{ backgroundColor: 'hsl(250 15% 65%)' }}>
                      {t.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-sm" style={{ color: 'hsl(250 15% 35%)' }}>{t.name}</p>
                      {t.location && (
                        <p className="text-xs" style={{ color: 'hsl(220 12% 55%)' }}>{t.location}</p>
                      )}
                    </div>
                    {t.featured && (
                      <Star className="w-4 h-4 ml-auto text-yellow-500 fill-yellow-500" />
                    )}
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-6">Be the first to share your experience!</p>
            </div>
          )}
          
          <div className="text-center">
            <Button
              size="lg"
              className="rounded-full px-10 py-6 text-base shadow-lg hover:shadow-xl transition-all"
              style={{ 
                backgroundColor: 'hsl(250 15% 55%)', 
                color: 'white',
                border: 'none'
              }}
              onClick={() => setShowSubmitDialog(true)}
              data-testid="share-testimonial-btn"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Share Your Experience
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing Section with soft dusty rose accent */}
      <section className="py-24 px-8 relative overflow-hidden" style={{ backgroundColor: 'hsl(15 35% 95%)' }}>
        {/* Decorative circles */}
        <div className="absolute top-10 right-10 w-32 h-32 rounded-full opacity-15" style={{ backgroundColor: 'hsl(250 12% 75%)' }}></div>
        <div className="absolute bottom-10 left-10 w-24 h-24 rounded-full opacity-10" style={{ backgroundColor: 'hsl(45 40% 75%)' }}></div>
        
        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl mb-8" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 40%)' }}>
            One Year of Growth
          </h2>
          
          <Card className="p-10 rounded-3xl border-0 shadow-lg mb-8" style={{ backgroundColor: 'white' }}>
            <p className="text-5xl font-light mb-2" style={{ color: 'hsl(250 15% 50%)' }}>$35.99</p>
            <p className="text-lg mb-6" style={{ color: 'hsl(220 12% 50%)' }}>per year</p>
            <div className="h-px w-24 mx-auto mb-6" style={{ backgroundColor: 'hsl(250 12% 82%)' }}></div>
            <ul className="text-left space-y-3 mb-8 max-w-xs mx-auto">
              <li className="flex items-center gap-3" style={{ color: 'hsl(220 12% 40%)' }}>
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs" style={{ backgroundColor: 'hsl(250 12% 92%)', color: 'hsl(250 15% 50%)' }}>✓</span>
                365 daily devotionals
              </li>
              <li className="flex items-center gap-3" style={{ color: 'hsl(220 12% 40%)' }}>
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs" style={{ backgroundColor: 'hsl(250 12% 92%)', color: 'hsl(250 15% 50%)' }}>✓</span>
                Personal journal entries
              </li>
              <li className="flex items-center gap-3" style={{ color: 'hsl(220 12% 40%)' }}>
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs" style={{ backgroundColor: 'hsl(250 12% 92%)', color: 'hsl(250 15% 50%)' }}>✓</span>
                Audio text-to-speech
              </li>
              <li className="flex items-center gap-3" style={{ color: 'hsl(220 12% 40%)' }}>
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs" style={{ backgroundColor: 'hsl(250 12% 92%)', color: 'hsl(250 15% 50%)' }}>✓</span>
                Bookmark favorites
              </li>
            </ul>
            <Button 
              data-testid="subscribe-btn"
              size="lg" 
              className="rounded-full px-12 py-6 text-lg w-full shadow-md hover:shadow-lg transition-shadow"
              style={{ backgroundColor: 'hsl(250 15% 60%)', color: 'white' }}
              onClick={() => navigate('/subscribe')}
            >
              Subscribe Now
            </Button>
          </Card>
          
          <p className="text-sm" style={{ color: 'hsl(220 12% 55%)' }}>
            Also available: $4.99/month • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-8" style={{ backgroundColor: 'hsl(220 15% 55%)' }}>
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex justify-center gap-6 mb-4">
            <a 
              href="/privacy" 
              className="text-sm text-white/70 hover:text-white transition-colors"
              onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}
            >
              Privacy Policy
            </a>
            <a 
              href="/terms" 
              className="text-sm text-white/70 hover:text-white transition-colors"
              onClick={(e) => { e.preventDefault(); navigate('/terms'); }}
            >
              Terms & Conditions
            </a>
          </div>
          <p className="text-sm text-white/70">&copy; 2025 Rooted. A space for contemplation and spiritual growth.</p>
        </div>
      </footer>

      {/* Testimonial Submission Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Share Your Experience</DialogTitle>
            <DialogDescription>
              We'd love to hear about your journey with Rooted Faith Journal
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="testimonial-name">Your Name *</Label>
              <Input
                id="testimonial-name"
                data-testid="testimonial-name-input"
                placeholder="Jane Smith"
                value={testimonialForm.name}
                onChange={(e) => setTestimonialForm({ ...testimonialForm, name: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="testimonial-location">Location (Optional)</Label>
              <Input
                id="testimonial-location"
                data-testid="testimonial-location-input"
                placeholder="Atlanta, GA"
                value={testimonialForm.location}
                onChange={(e) => setTestimonialForm({ ...testimonialForm, location: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Rating *</Label>
              <div className="flex gap-1 mt-2">
                {renderStars(
                  testimonialForm.rating,
                  true,
                  (rating) => setTestimonialForm({ ...testimonialForm, rating })
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="testimonial-content">Your Testimonial *</Label>
              <Textarea
                id="testimonial-content"
                data-testid="testimonial-content-input"
                placeholder="Share how Rooted has impacted your faith journey..."
                value={testimonialForm.content}
                onChange={(e) => setTestimonialForm({ ...testimonialForm, content: e.target.value })}
                rows={4}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Minimum 10 characters</p>
            </div>

            <div>
              <Label htmlFor="testimonial-email">Email (Optional)</Label>
              <Input
                id="testimonial-email"
                data-testid="testimonial-email-input"
                type="email"
                placeholder="For follow-up (not displayed publicly)"
                value={testimonialForm.email}
                onChange={(e) => setTestimonialForm({ ...testimonialForm, email: e.target.value })}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Your email won't be shown publicly</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitTestimonial} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Testimonial'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
